import { supabase } from "@/lib/supabase";
import { validateImageFile } from "@/lib/questImages";
import { useAuthStore } from "@/stores/useAuthStore";
import { toUserTier, viewTier, type ViewTier } from "@/lib/tier";
import type { UserRole } from "@/lib/types";
import {
  rankInvitableUsers,
  sortMembersByRole,
  userSearchTerm,
  USER_SEARCH_LIMIT,
  sortOrgsByName,
  ORG_IMAGES_BUCKET,
  orgImagePath,
  type AdminOrganizationRow,
  type CreateOrganizationInput,
  type InvitableUser,
  type InviteRole,
  type LinkInviteRole,
  type MemberPresence,
  type MoveQuestResult,
  type MyOrganization,
  type Organization,
  type OrgImageKind,
  type OrgInvite,
  type OrgInvitePreview,
  type OrgJoinRequest,
  type OrgKind,
  type OrgMember,
  type OrgOpenQuest,
  type OrgRole,
  type PendingOrgInvite,
  type UpdateOrganizationInput,
} from "@/lib/organizations";
import type { IOrganizationService } from "@/services/interfaces/IOrganizationService";
import {
  isOpenOrgQuestRow,
  mapOrgOpenQuestRow,
  ORG_OPEN_QUEST_COLUMNS,
  orgImageUrl,
  type OrgOpenQuestRow,
} from "./orgMapping";

/**
 * organizations is on column grants (no `verification_note`, no
 * `created_by` for anon), so every read names its columns — `select=*`
 * would be refused outright.
 */
const ORG_COLUMNS =
  "id, slug, name, kind, bio, website, location_city, location_country, logo_path, banner_path, verified_at, created_at";

/** Cross-user display goes through public_profiles, never profiles (owner-or-admin RLS). */
const MEMBER_PROFILE = "profile:public_profiles!organization_members_user_id_fkey(display_name, handle, avatar_url)";
const REQUEST_PROFILE = "profile:public_profiles!organization_join_requests_user_id_fkey(display_name, handle, avatar_url)";

interface OrgRow {
  id: string;
  slug: string;
  name: string;
  kind: string;
  bio: string | null;
  website: string | null;
  location_city: string | null;
  location_country: string | null;
  logo_path: string | null;
  banner_path: string | null;
  verified_at: string | null;
  created_at: string;
}

interface ProfileEmbed {
  display_name: string | null;
  handle: string | null;
  avatar_url: string | null;
}

function mapOrg(row: OrgRow): Organization {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    kind: row.kind as OrgKind,
    bio: row.bio,
    website: row.website,
    locationCity: row.location_city,
    locationCountry: row.location_country,
    logoPath: row.logo_path,
    bannerPath: row.banner_path,
    logoUrl: orgImageUrl(row.logo_path),
    bannerUrl: orgImageUrl(row.banner_path),
    verified: row.verified_at !== null,
    createdAt: row.created_at,
  };
}

/** Keep the RPC's stable `org_*` code as the message; orgErrorKey maps it. */
function fail(error: { message: string; details?: string | null }): never {
  // details carries extra context some codes need (org_rate_limited: retry_at).
  throw Object.assign(new Error(error.message), { details: error.details ?? null });
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) fail(error);
  return data as T;
}

export class SupabaseOrganizationService implements IOrganizationService {
  // ─── Reading ──────────────────────────────────────────────
  async getBySlug(slug: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from("organizations")
      .select(ORG_COLUMNS)
      .eq("slug", slug.toLowerCase())
      .maybeSingle();
    if (error) fail(error);
    return data ? mapOrg(data as OrgRow) : null;
  }

  async getById(id: string): Promise<Organization | null> {
    const { data, error } = await supabase.from("organizations").select(ORG_COLUMNS).eq("id", id).maybeSingle();
    if (error) fail(error);
    return data ? mapOrg(data as OrgRow) : null;
  }

  async listOpenQuests(orgId: string, chainId: number): Promise<OrgOpenQuest[]> {
    // Only statuses anon may already read (anon_select_active_quests), so the
    // logged-out org page gets the same list.
    const { data, error } = await supabase
      .from("quests")
      .select(ORG_OPEN_QUEST_COLUMNS)
      .eq("organization_id", orgId)
      .eq("chain_id", chainId)
      .in("status", ["published", "negotiating", "active"])
      // A stock listing shows once (its anchor); its per-worker clones don't.
      .or("template_id.is.null,is_template_anchor.eq.true")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) fail(error);
    return ((data ?? []) as unknown as OrgOpenQuestRow[]).filter(isOpenOrgQuestRow).map(mapOrgOpenQuestRow);
  }

  async listMine(userId: string): Promise<MyOrganization[]> {
    const { data, error } = await supabase
      .from("organization_members")
      .select(`role, organization:organizations(${ORG_COLUMNS})`)
      .eq("user_id", userId);
    if (error) fail(error);
    return sortOrgsByName(
      ((data ?? []) as unknown as { role: OrgRole; organization: OrgRow | null }[])
        .filter((r) => r.organization)
        .map((r) => ({ ...mapOrg(r.organization!), myRole: r.role })),
    );
  }

  async listForUser(userId: string): Promise<Organization[]> {
    const { data, error } = await supabase
      .from("organization_members")
      .select(`organization:organizations(${ORG_COLUMNS})`)
      .eq("user_id", userId);
    if (error) fail(error);
    return ((data ?? []) as unknown as { organization: OrgRow | null }[])
      .filter((r) => r.organization)
      .map((r) => mapOrg(r.organization!));
  }

  async listMembers(orgId: string): Promise<OrgMember[]> {
    const { data, error } = await supabase
      .from("organization_members")
      .select(`user_id, role, joined_at, ${MEMBER_PROFILE}`)
      .eq("organization_id", orgId)
      .order("joined_at", { ascending: true });
    if (error) fail(error);
    const rows = (data ?? []) as unknown as {
      user_id: string; role: OrgRole; joined_at: string; profile: ProfileEmbed | null;
    }[];
    return sortMembersByRole(
      rows.map((r) => ({
        userId: r.user_id,
        role: r.role,
        joinedAt: r.joined_at,
        // A denied embed is 200 + null: render a translated generic, never the DID.
        displayName: r.profile?.display_name ?? null,
        handle: r.profile?.handle ?? null,
        avatarUrl: r.profile?.avatar_url ?? null,
      })),
    );
  }

  async getMemberPresence(orgId: string): Promise<Record<string, MemberPresence>> {
    const rows = await rpc<{ user_id: string; bucket: MemberPresence["bucket"]; amount: number | null }[]>(
      "org_member_presence", { p_org: orgId });
    return Object.fromEntries((rows ?? []).map((r) => [r.user_id, { bucket: r.bucket, amount: r.amount }]));
  }

  // ─── Lifecycle ────────────────────────────────────────────
  async create(input: CreateOrganizationInput): Promise<{ id: string; slug: string }> {
    const id = await rpc<string>("create_organization", {
      p_name: input.name,
      p_slug: input.slug,
      p_kind: input.kind,
      p_bio: input.bio ?? null,
      p_website: input.website ?? null,
      p_location_city: input.locationCity ?? null,
      p_location_country: input.locationCountry ?? null,
    });
    return { id, slug: input.slug.trim().toLowerCase() };
  }

  async update(orgId: string, input: UpdateOrganizationInput): Promise<void> {
    const patch: Record<string, unknown> = {};
    if (input.name !== undefined) patch.name = input.name;
    if (input.kind !== undefined) patch.kind = input.kind;
    if (input.bio !== undefined) patch.bio = input.bio || null;
    if (input.website !== undefined) patch.website = input.website || null;
    if (input.locationCity !== undefined) patch.location_city = input.locationCity || null;
    if (input.locationCountry !== undefined) patch.location_country = input.locationCountry || null;
    // RLS hides the row from a non-admin, which PostgREST reports as 0 rows,
    // not an error: ask for the row back so a refusal is not silent.
    const { data, error } = await supabase.from("organizations").update(patch).eq("id", orgId).select("id");
    if (error) fail(error);
    if (!data?.length) throw new Error("org_not_admin");
  }

  async setImage(orgId: string, kind: OrgImageKind, file: File): Promise<void> {
    const check = validateImageFile(file);
    if (!check.ok) throw new Error(check.reason === "too_large" ? "org_image_too_large" : "org_image_bad_type");

    const { data: before } = await supabase
      .from("organizations").select("logo_path, banner_path").eq("id", orgId).maybeSingle();
    const path = orgImagePath(orgId, kind, file.type, crypto.randomUUID());
    const { error: uploadError } = await supabase.storage
      .from(ORG_IMAGES_BUCKET).upload(path, file, { contentType: file.type });
    if (uploadError) throw new Error("org_image_upload_failed");

    const column = kind === "logo" ? "logo_path" : "banner_path";
    const { data, error } = await supabase
      .from("organizations").update({ [column]: path }).eq("id", orgId).select("id");
    if (error || !data?.length) {
      await supabase.storage.from(ORG_IMAGES_BUCKET).remove([path]);
      throw new Error(error?.message ?? "org_not_admin");
    }
    await this.removeOld((before as Record<string, string | null> | null)?.[column] ?? null, orgId);
  }

  async clearImage(orgId: string, kind: OrgImageKind): Promise<void> {
    const column = kind === "logo" ? "logo_path" : "banner_path";
    const { data: before } = await supabase
      .from("organizations").select(column).eq("id", orgId).maybeSingle();
    const { data, error } = await supabase
      .from("organizations").update({ [column]: null }).eq("id", orgId).select("id");
    if (error) fail(error);
    if (!data?.length) throw new Error("org_not_admin");
    await this.removeOld((before as Record<string, string | null> | null)?.[column] ?? null, orgId);
  }

  /** Best effort, after the row moved on; only objects in this org's folder. */
  private async removeOld(path: string | null, orgId: string): Promise<void> {
    if (!path || !path.startsWith(`${orgId}/`) || path.includes("..")) return;
    const { error } = await supabase.storage.from(ORG_IMAGES_BUCKET).remove([path]);
    if (error) console.warn("[org-images] replaced image not removed", path, error);
  }

  async remove(orgId: string): Promise<void> {
    await rpc("delete_organization", { p_org: orgId });
  }

  // ─── Invites ──────────────────────────────────────────────
  async createLinkInvite(orgId: string): Promise<{ inviteId: string; code: string }> {
    // Links admit as Simple only; the RPC lowers anything else anyway.
    const rows = await rpc<{ invite_id: string; code: string }[]>("create_org_invite", {
      p_org: orgId, p_kind: "link", p_role: "simple" satisfies LinkInviteRole, p_email: null,
    });
    const row = rows[0]!;
    return { inviteId: row.invite_id, code: row.code };
  }

  async createEmailInvite(orgId: string, email: string, role: InviteRole): Promise<{ inviteId: string }> {
    const rows = await rpc<{ invite_id: string }[]>("create_org_invite", {
      p_org: orgId, p_kind: "email", p_role: role, p_email: email,
    });
    return { inviteId: rows[0]!.invite_id };
  }

  async createUserInvite(orgId: string, userId: string, role: InviteRole): Promise<{ inviteId: string }> {
    const inviteId = await rpc<string>("create_org_user_invite", { p_org: orgId, p_user: userId, p_role: role });
    return { inviteId };
  }

  async searchUsersToInvite(orgId: string, query: string): Promise<InvitableUser[]> {
    const term = userSearchTerm(query);
    if (!term) return [];
    const session = useAuthStore.getState().session;
    const me = session?.userId ?? null;
    // userSearchTerm strips every character the or=(…) grammar treats as
    // syntax, so the term interpolates safely.
    let q = supabase
      .from("public_profiles")
      .select("id, display_name, handle, avatar_url")
      .or(`handle.ilike.*${term}*,display_name.ilike.*${term}*`)
      .limit(USER_SEARCH_LIMIT * 3);   // a pool for rankInvitableUsers to order
    if (me) q = q.neq("id", me);
    const { data, error } = await q;
    if (error) fail(error);
    const rows = (data ?? []) as { id: string; display_name: string | null; handle: string | null; avatar_url: string | null }[];
    if (rows.length === 0) return [];
    // Membership is readable by every signed-in user; ask only about the hits.
    const members = await supabase.from("organization_members").select("user_id")
      .eq("organization_id", orgId).in("user_id", rows.map((r) => r.id));
    if (members.error) fail(members.error);
    const memberIds = new Set((members.data ?? []).map((m: { user_id: string }) => m.user_id));
    const tiers = session?.role === "admin" ? await tiersFor(rows.map((r) => r.id)) : new Map<string, ViewTier>();
    return rankInvitableUsers(
      rows.map((r) => ({
        userId: r.id, displayName: r.display_name, handle: r.handle, avatarUrl: r.avatar_url,
        isMember: memberIds.has(r.id), tier: tiers.get(r.id),
      })),
      term,
    ).slice(0, USER_SEARCH_LIMIT);
  }

  async listInvites(orgId: string): Promise<OrgInvite[]> {
    const rows = await rpc<{
      id: string; kind: OrgInvite["kind"]; role: OrgInvite["role"]; email_hint: string | null;
      code: string | null; created_at: string; expires_at: string; state: OrgInvite["state"];
      // Absent until 20261009100000 is applied.
      invitee_id?: string | null; invitee_name?: string | null; invitee_handle?: string | null;
      invitee_avatar_url?: string | null;
    }[]>("org_invite_list", { p_org: orgId });
    return (rows ?? []).map((r) => ({
      id: r.id, kind: r.kind, role: r.role, emailHint: r.email_hint, code: r.code,
      invitee: r.invitee_id
        ? { userId: r.invitee_id, displayName: r.invitee_name ?? null, handle: r.invitee_handle ?? null,
            avatarUrl: r.invitee_avatar_url ?? null }
        : null,
      createdAt: r.created_at, expiresAt: r.expires_at, state: r.state,
    }));
  }

  async revokeInvite(inviteId: string): Promise<void> {
    await rpc("revoke_org_invite", { p_invite: inviteId });
  }

  async previewLinkInvite(code: string): Promise<OrgInvitePreview | null> {
    const rows = await rpc<{ organization_id: string; slug: string; name: string; logo_path: string | null; verified: boolean }[]>(
      "org_invite_preview", { p_code: code });
    const r = rows?.[0];
    return r ? { organizationId: r.organization_id, slug: r.slug, name: r.name, logoUrl: orgImageUrl(r.logo_path), verified: r.verified } : null;
  }

  async acceptLinkInvite(code: string): Promise<{ organizationId: string; slug: string }> {
    const rows = await rpc<{ organization_id: string; slug: string }[]>("accept_org_invite", { p_code: code });
    return { organizationId: rows[0]!.organization_id, slug: rows[0]!.slug };
  }

  async acceptEmailInvite(inviteId: string): Promise<{ organizationId: string; slug: string }> {
    const rows = await rpc<{ organization_id: string; slug: string }[]>("accept_org_email_invite", { p_invite: inviteId });
    return { organizationId: rows[0]!.organization_id, slug: rows[0]!.slug };
  }

  async listMyPendingInvites(): Promise<PendingOrgInvite[]> {
    const rows = await rpc<{
      invite_id: string; organization_id: string; slug: string; name: string; logo_path: string | null;
      verified: boolean; role: InviteRole; invited_by_name: string | null; expires_at: string;
    }[]>("my_pending_org_invites");
    return (rows ?? []).map((r) => ({
      inviteId: r.invite_id, organizationId: r.organization_id, slug: r.slug, name: r.name,
      logoUrl: orgImageUrl(r.logo_path), verified: r.verified, role: r.role,
      invitedByName: r.invited_by_name, expiresAt: r.expires_at,
    }));
  }

  // ─── Join requests ────────────────────────────────────────
  async requestToJoin(orgId: string, message: string | null): Promise<string> {
    return rpc<string>("request_to_join_org", { p_org: orgId, p_message: message });
  }

  async cancelJoinRequest(requestId: string): Promise<void> {
    await rpc("cancel_org_join_request", { p_request: requestId });
  }

  async getMyJoinRequest(orgId: string, userId: string): Promise<OrgJoinRequest | null> {
    const { data, error } = await supabase
      .from("organization_join_requests")
      .select(`id, organization_id, user_id, message, status, created_at, ${REQUEST_PROFILE}`)
      .eq("organization_id", orgId)
      .eq("user_id", userId)
      .eq("status", "pending")
      .maybeSingle();
    if (error) fail(error);
    return data ? mapRequest(data as unknown as RequestRow) : null;
  }

  async listJoinRequests(orgId: string): Promise<OrgJoinRequest[]> {
    const { data, error } = await supabase
      .from("organization_join_requests")
      .select(`id, organization_id, user_id, message, status, created_at, ${REQUEST_PROFILE}`)
      .eq("organization_id", orgId)
      .eq("status", "pending")
      .order("created_at", { ascending: true });
    if (error) fail(error);
    return ((data ?? []) as unknown as RequestRow[]).map(mapRequest);
  }

  async decideJoinRequest(requestId: string, approve: boolean): Promise<void> {
    await rpc("decide_org_join_request", { p_request: requestId, p_approve: approve });
  }

  // ─── Members ──────────────────────────────────────────────
  async setMemberRole(orgId: string, userId: string, role: InviteRole): Promise<void> {
    await rpc("set_org_member_role", { p_org: orgId, p_user: userId, p_role: role });
  }

  async transferOwnership(orgId: string, userId: string): Promise<void> {
    await rpc("transfer_org_ownership", { p_org: orgId, p_user: userId });
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    await rpc("remove_org_member", { p_org: orgId, p_user: userId });
  }

  async leave(orgId: string): Promise<void> {
    await rpc("leave_organization", { p_org: orgId });
  }

  // ─── Quests ───────────────────────────────────────────────
  async moveQuestToOrganization(questId: string, orgId: string): Promise<MoveQuestResult> {
    const r = await rpc<{ moved_quests: number; reissued_certificates: number }>("move_quest_to_organization", {
      p_quest_id: questId,
      p_organization_id: orgId,
    });
    return { movedQuests: r.moved_quests, reissuedCertificates: r.reissued_certificates };
  }

  // ─── Presence ─────────────────────────────────────────────
  async touchPresence(): Promise<void> {
    await rpc("touch_presence");
  }

  async getShareActivity(): Promise<boolean> {
    return (await rpc<boolean | null>("get_my_share_activity")) ?? true;
  }

  async setShareActivity(share: boolean): Promise<void> {
    await rpc("set_share_activity", { p_share: share });
  }

  // ─── weXare admin ─────────────────────────────────────────
  async adminList(search: string | null): Promise<AdminOrganizationRow[]> {
    const rows = await rpc<{
      id: string; slug: string; name: string; kind: string; website: string | null; logo_path: string | null;
      location_city: string | null; location_country: string | null; created_at: string;
      verified_at: string | null; verification_note: string | null; member_count: number;
      owner_handle: string | null; owner_email_matches_website: boolean;
    }[]>("admin_list_organizations", { p_search: search, p_limit: 200 });
    return (rows ?? []).map((r) => ({
      id: r.id, slug: r.slug, name: r.name, kind: r.kind as OrgKind, website: r.website,
      logoUrl: orgImageUrl(r.logo_path), locationCity: r.location_city, locationCountry: r.location_country,
      createdAt: r.created_at, verifiedAt: r.verified_at, verificationNote: r.verification_note,
      memberCount: Number(r.member_count), ownerHandle: r.owner_handle,
      ownerEmailMatchesWebsite: r.owner_email_matches_website,
    }));
  }

  async adminSetVerification(orgId: string, verified: boolean, note: string | null): Promise<void> {
    await rpc("admin_set_organization_verification", { p_org: orgId, p_verified: verified, p_note: note });
  }

  async adminRename(orgId: string, name: string | null, slug: string | null): Promise<void> {
    await rpc("admin_update_organization", { p_org: orgId, p_name: name, p_slug: slug });
  }
}

interface RequestRow {
  id: string;
  organization_id: string;
  user_id: string;
  message: string | null;
  status: OrgJoinRequest["status"];
  created_at: string;
  profile: ProfileEmbed | null;
}

function mapRequest(r: RequestRow): OrgJoinRequest {
  return {
    id: r.id,
    organizationId: r.organization_id,
    userId: r.user_id,
    message: r.message,
    status: r.status,
    createdAt: r.created_at,
    displayName: r.profile?.display_name ?? null,
    handle: r.profile?.handle ?? null,
    avatarUrl: r.profile?.avatar_url ?? null,
  };
}

/**
 * Platform tiers for a page of search hits, for an admin viewer. profiles is
 * owner-or-admin under RLS, so only an admin gets other people's rows; a
 * failed read leaves the badges off rather than breaking the search.
 */
async function tiersFor(ids: string[]): Promise<Map<string, ViewTier>> {
  const { data, error } = await supabase.from("profiles").select("id, role, tier").in("id", ids);
  if (error || !data) return new Map();
  return new Map((data as { id: string; role: UserRole; tier: unknown }[])
    .map((r) => [r.id, viewTier(r.role, toUserTier(r.tier))]));
}
